import { useEffect, useMemo, useState } from "react";
import type { DiagnosisSeverity, PlantDiagnosisRecord, PlantPhoto } from "../../shared/types";
import { diagnosePlant } from "../api";

function formatShortDate(ms: number) {
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(ms));
  } catch {
    return "—";
  }
}

function severityLabel(severity: DiagnosisSeverity) {
  switch (severity) {
    case "low":
      return "Low concern";
    case "medium":
      return "Medium concern";
    case "high":
      return "High concern";
    default:
      return "Uncertain";
  }
}

function defaultSelectedIds(sorted: PlantPhoto[]): Set<string> {
  const n = Math.min(4, sorted.length);
  return new Set(sorted.slice(0, n).map((p) => p.id));
}

export function PlantHealthPanel(props: {
  plantId: string;
  photos: PlantPhoto[];
  diagnosis: PlantDiagnosisRecord | null;
  diagnoseBusy: boolean;
  onDiagnose: (d: PlantDiagnosisRecord) => void;
  onDiagnoseBusy: (busy: boolean) => void;
}) {
  const { plantId, photos, diagnosis, diagnoseBusy, onDiagnose, onDiagnoseBusy } = props;

  const sorted = useMemo(() => [...photos].sort((a, b) => b.created_at - a.created_at), [photos]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => defaultSelectedIds(sorted));

  useEffect(() => {
    setSelectedIds((prev) => {
      const valid = new Set(sorted.map((p) => p.id));
      const kept = [...prev].filter((id) => valid.has(id));
      if (kept.length > 0) return new Set(kept);
      return defaultSelectedIds(sorted);
    });
  }, [sorted]);

  const selectedCount = selectedIds.size;
  const maxPhotos = sorted.length;

  function togglePhoto(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size <= 1) return next;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** Oldest first so AI can read growth over time. */
  const selectedPhotoIds = [...sorted]
    .filter((p) => selectedIds.has(p.id))
    .reverse()
    .map((p) => p.id);

  return (
    <div className="panel" style={{ borderColor: "rgba(125, 211, 252, 0.35)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Plant health check</h2>
        <button
          type="button"
          className="btn btn-primary"
          disabled={diagnoseBusy || selectedCount === 0}
          onClick={async () => {
            onDiagnoseBusy(true);
            try {
              const { diagnosis: d } = await diagnosePlant(plantId, selectedPhotoIds);
              onDiagnose(d);
            } catch (err) {
              alert(err instanceof Error ? err.message : "Health check failed");
            } finally {
              onDiagnoseBusy(false);
            }
          }}
        >
          {diagnoseBusy ? "Analyzing…" : "Run AI health check"}
        </button>
      </div>

      {maxPhotos > 0 ? (
        <div className="health-photo-picker" style={{ marginTop: 12 }}>
          <p className="muted" style={{ margin: "0 0 10px", fontSize: "0.85rem" }}>
            <strong>{selectedCount}</strong> of {maxPhotos} selected — tap thumbnails to include or exclude.
          </p>
          <div className="photo-gallery health-photo-gallery" role="group" aria-label="Photos for health check">
            {sorted.map((ph) => {
              const selected = selectedIds.has(ph.id);
              return (
                <button
                  key={ph.id}
                  type="button"
                  className={`photo-thumb health-photo-thumb${selected ? " photo-thumb--active health-photo-thumb--selected" : ""}`}
                  onClick={() => togglePhoto(ph.id)}
                  aria-pressed={selected}
                  aria-label={`${selected ? "Exclude" : "Include"} photo from ${formatShortDate(ph.created_at)}`}
                >
                  <img src={ph.url} alt="" loading="lazy" decoding="async" />
                  {selected ? <span className="photo-thumb__badge">✓</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
        Not a substitute for in-person inspection.
      </p>

      {diagnosis ? (
        <div className="stack" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <span className="pill">{severityLabel(diagnosis.result.severity)}</span>
            <span className="muted" style={{ fontSize: "0.85rem" }}>
              {formatShortDate(diagnosis.created_at)} · {diagnosis.photo_ids.length}{" "}
              {diagnosis.photo_ids.length === 1 ? "photo" : "photos"} · ~
              {Math.round(diagnosis.result.confidence * 100)}% confidence
            </span>
          </div>
          <p style={{ margin: 0 }}>{diagnosis.result.summary}</p>
          {diagnosis.result.growth_notes ? (
            <p style={{ margin: 0 }}>
              <strong>Growth:</strong> {diagnosis.result.growth_notes}
            </p>
          ) : null}
          <div>
            <strong>Possible issues</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
              {diagnosis.result.possible_issues.map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          </div>
          <div>
            <strong>Suggested actions</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
              {diagnosis.result.recommended_actions.map((action, i) => (
                <li key={i}>{action}</li>
              ))}
            </ul>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
            {diagnosis.result.disclaimer}
          </p>
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
          No health check yet. Run AI when something looks off or to compare growth across photos.
        </p>
      )}
    </div>
  );
}
